/**
 * Client-side mirror of the server's project-key scheme.
 *
 * The server is the authority: it derives the key when one is not sent, refuses
 * a taken one with a 422, and appends a numeric suffix on collision. This
 * mirror exists so the form can SHOW the key it is about to get and warn about
 * a collision before the round trip — never to decide anything. Anywhere the
 * two disagree, the server's answer is the one rendered.
 *
 * Kept in step with DeriveProjectKey / deriveNewProjectKey in
 * api/internal/handler/{issue,project}.go.
 */

export const MIN_PROJECT_KEY_LENGTH = 2
export const MAX_PROJECT_KEY_LENGTH = 10
/** A derived key stays short so issue keys read as "TSK-14", not "MYPROJECT-14". */
const MAX_DERIVED_KEY_LENGTH = 5
const FALLBACK_KEY = 'TSK'

/** Splits on anything that is not A-Z0-9, exactly as the Go side does. */
function keyWords(name: string): string[] {
  return name
    .toUpperCase()
    .normalize('NFKD')
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)
}

/**
 * The key the server would derive from this name: initials for a multi-word
 * name ("Mobile Apps" → "MA"), otherwise the leading characters ("Taskly" →
 * "TASK").
 */
export function deriveProjectKey(name: string): string {
  const words = keyWords(name)
  const initials = words.map((word) => word[0]).join('')

  let base = initials.length >= 2 ? initials : words.join('').slice(0, 4)
  if (!base) base = FALLBACK_KEY
  base = base.slice(0, MAX_DERIVED_KEY_LENGTH)
  while (base.length < MIN_PROJECT_KEY_LENGTH) base += base.slice(-1)
  return base
}

/**
 * The suggestion shown in the form: the derived key, stepped past any key the
 * workspace already uses, the way the server steps past a collision.
 */
export function suggestProjectKey(name: string, taken: readonly string[]): string {
  const base = deriveProjectKey(name)
  const used = new Set(taken.map((key) => key.toUpperCase()))
  if (!used.has(base)) return base

  for (let attempt = 2; attempt < 64; attempt += 1) {
    const suffix = String(attempt)
    const trimmed = base.slice(0, Math.max(1, Math.min(base.length, MAX_PROJECT_KEY_LENGTH - suffix.length)))
    const candidate = `${trimmed}${suffix}`
    if (!used.has(candidate)) return candidate
  }
  return base
}

/** Normalises what someone typed into the shape the server accepts. */
export function normalizeProjectKeyInput(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_PROJECT_KEY_LENGTH)
}

/**
 * Why this key would be refused, or null when it looks acceptable.
 *
 * `taken` is the workspace's current keys; a match is reported before the
 * request rather than after, because a rejected create loses the typed form on
 * every client that does not preserve it.
 */
export function projectKeyProblem(key: string, taken: readonly string[]): string | null {
  if (!key) return null
  if (key.length < MIN_PROJECT_KEY_LENGTH || key.length > MAX_PROJECT_KEY_LENGTH) {
    return `Use ${MIN_PROJECT_KEY_LENGTH} to ${MAX_PROJECT_KEY_LENGTH} letters or digits.`
  }
  if (!/^[A-Z][A-Z0-9]*$/.test(key)) {
    return 'Start with a letter, then letters or digits only.'
  }
  if (taken.some((existing) => existing.toUpperCase() === key)) {
    return 'That key is already used in this workspace.'
  }
  return null
}
